'use client';

import React, { useEffect, useState, useContext, useLayoutEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { supabase } from '../../supabaseClient';
import { GoogleMap, useLoadScript, PolygonF } from '@react-google-maps/api';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import Layout from '../Layout';
import ReportBuilder from '../../utils/ReportBuilder';
import DetailsTab from './DetailsTab';
import ProjectFiles from './ProjectFiles';
import ExportHistory from './ExportHistory';
import ProjectAnalytics from './ProjectAnalytics';
import OnStationModal from '../Integrations/OnStationModal';
import {
  ArrowLongLeftIcon,
  ArrowLongRightIcon,
  ChevronDownIcon,
  EllipsisVerticalIcon,
  CheckIcon,
  ExclamationCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/20/solid';
import { BoltIcon, LightBulbIcon, MoonIcon, SunIcon } from '@heroicons/react/24/outline';
import { aubergeine, nightMode, retro } from '../../styles/mapStyles';
import DensityReportGenerator from '../../components/DensityReportGenerator';
import { downloadDensityReport } from '../../utils/densityReportUtils';

// Performance constants
const MAX_RECORDS = 10000;
const MAX_MAP_MARKERS = 10000;
const COMPACTION_PASS_THRESHOLD = 92.0;

// Default quality thresholds
const DEFAULT_THRESHOLDS = {
  compaction: {
    pass: 92.0,
    warning: 88.0,
    fail: 0.0
  },
  density: {
    pass: 145.0,
    warning: 140.0,
    fail: 0.0
  }
};

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const customMarkerIcon = {
  path: 'M10 20c-5.523 0-10-4.477-10-10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10z',
  fillColor: '#FF0000',
  fillOpacity: 1,
  strokeWeight: 2,
  strokeColor: '#FFFFFF',
  scale: 0.6,
};

const activeMarkerIcon = {
  ...customMarkerIcon,
  fillColor: '#00FF00',
};

// Quality-based marker icons
const qualityMarkerIcons = {
  pass: {
    ...customMarkerIcon,
    fillColor: '#10B981', // Green
  },
  warning: {
    ...customMarkerIcon,
    fillColor: '#F59E0B', // Orange
  },
  fail: {
    ...customMarkerIcon,
    fillColor: '#EF4444', // Red
  },
  unknown: {
    ...customMarkerIcon,
    fillColor: '#6B7280', // Gray
  }
};

// Custom cluster renderer function
const createClusterRenderer = () => {
  if (!window.google || !window.google.maps) {
    console.warn('Google Maps not loaded yet');
    return null;
  }

  return {
    render: ({ count, position }) => {
      const color = count > 500 ? "#dc2626" : count > 100 ? "#ea580c" : count > 25 ? "#d97706" : "#059669";
      const size = count > 500 ? 70 : count > 100 ? 60 : count > 25 ? 50 : 40;
      
      return new window.google.maps.Marker({
        position,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          fillColor: color,
          fillOpacity: 0.8,
          strokeWeight: 2,
          strokeColor: "#ffffff",
          scale: size / 2,
        },
        label: {
          text: String(count),
          color: "white",
          fontSize: count > 100 ? "14px" : "12px",
          fontWeight: "bold",
        },
        title: `${count} measurements`,
        zIndex: 1000,
      });
    },
  };
};

function Tooltip({ message, children }) {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div 
      className="relative inline-block"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {isVisible && message && (
        <div className="absolute z-50 w-64 px-2 py-1 text-xs text-white bg-gray-800 rounded-md shadow-lg right-full mr-2 top-0">
          {message}
        </div>
      )}
    </div>
  );
}

export default function MappedProject() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  // Core data states
  const [measurements, setMeasurements] = useState([]);
  const [mapMarkers, setMapMarkers] = useState([]); // Separate markers for map display
  const [loading, setLoading] = useState(true);
  const [memberIds, setMemberIds] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);

  // Project states
  const [projectData, setProjectData] = useState(null);
  const [projectPolygon, setProjectPolygon] = useState([]);
  const [organizationId, setOrganizationId] = useState(null);

  // UI states
  const [selectedMeasurements, setSelectedMeasurements] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [measurementsPerPage] = useState(25); // Increased from 10
  const [activeTab, setActiveTab] = useState('Measurements');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedDetailsMeasurement, setSelectedDetailsMeasurement] = useState(null);

  // Filter states
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedOperator, setSelectedOperator] = useState('');
  const [operators, setOperators] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Map states
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: 0, lng: 0 });
  const [activeMarkerId, setActiveMarkerId] = useState(null);

  // Quality control states
  const [qualityControlExpanded, setQualityControlExpanded] = useState(false);
  const [qualityAttribute, setQualityAttribute] = useState('compaction'); // 'compaction' or 'density'
  const [qualityThresholds, setQualityThresholds] = useState(DEFAULT_THRESHOLDS);
  const [tempThresholds, setTempThresholds] = useState(DEFAULT_THRESHOLDS);

  // Modal states
  const [showReportBuilder, setShowReportBuilder] = useState(false);
  const [showOnStationModal, setShowOnStationModal] = useState(false);

  // Actions states
  const [actionsDropdown, setActionsDropdown] = useState(null);
  const [actionSuccess, setActionSuccess] = useState(false);
  const [availableIntegrations, setAvailableIntegrations] = useState({});
  const [sentIntegrations, setSentIntegrations] = useState({});

  // Refs
  const checkbox = useRef();
  const actionsMenuRef = useRef(null);
  const mapRef = useRef(null);
  const markerClustererRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);
  const debounceTimeoutRef = useRef(null);

  // Constants
  const [checked, setChecked] = useState(false);
  const [indeterminate, setIndeterminate] = useState(false);
  const integrations = ['OnStation','HaulHub'];
  const DEBOUNCE_MS = 800;

  const [mapStyle, setMapStyle] = useState([]);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
const [measurementToDelete, setMeasurementToDelete] = useState(null);
const [deletingMeasurement, setDeletingMeasurement] = useState(false);
const [reportGenerating, setReportGenerating] = useState(false);


  const { isLoaded } = useLoadScript({
    googleMapsApiKey: 'AIzaSyBBfpoVAWYZaujXG5t93mFIJKqk3cwHynw',
  });

  const handleGenerateReport = async (projectData, measurements, filename, options) => {
    setReportGenerating(true);
    try {
      await downloadDensityReport(projectData, measurements, filename, options);
      
      // Optional: Show success message
      setActionSuccess(true);
      setTimeout(() => setActionSuccess(false), 3000);
      
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate report. Please try again.');
    } finally {
      setReportGenerating(false);
    }
  };

  // Calculate compaction pass rate
  const calculateCompactionPassRate = useCallback((measurementsData) => {
    if (!measurementsData || measurementsData.length === 0) {
      return { passRate: 0, totalMeasurements: 0, passingMeasurements: 0 };
    }

    const validMeasurements = measurementsData.filter(m => 
      m.Compaction !== null && m.Compaction !== undefined && !isNaN(m.Compaction)
    );

    if (validMeasurements.length === 0) {
      return { passRate: 0, totalMeasurements: measurementsData.length, passingMeasurements: 0 };
    }

    const passingMeasurements = validMeasurements.filter(m => 
      m.Compaction >= COMPACTION_PASS_THRESHOLD
    ).length;

    const passRate = (passingMeasurements / validMeasurements.length) * 100;

    return {
      passRate: Math.round(passRate * 10) / 10,
      totalMeasurements: validMeasurements.length,
      passingMeasurements
    };
  }, []);

  // Determine quality status based on attribute and thresholds
  const getQualityStatus = useCallback((measurement, attribute, thresholds) => {
    const value = measurement[attribute === 'compaction' ? 'Compaction' : 'Density'];
    
    if (value === null || value === undefined || isNaN(value)) {
      return 'unknown';
    }

    const limits = thresholds[attribute];
    
    if (value >= limits.pass) {
      return 'pass';
    } else if (value >= limits.warning) {
      return 'warning';
    } else {
      return 'fail';
    }
  }, []);

  // Calculate quality statistics for current view
  const calculateQualityStats = useCallback((measurementsData, attribute, thresholds) => {
    if (!measurementsData || measurementsData.length === 0) {
      return { pass: 0, warning: 0, fail: 0, unknown: 0, total: 0, passRate: 0 };
    }

    const stats = { pass: 0, warning: 0, fail: 0, unknown: 0, total: measurementsData.length };

    measurementsData.forEach(measurement => {
      const status = getQualityStatus(measurement, attribute, thresholds);
      stats[status]++;
    });

    stats.passRate = stats.total > 0 ? Math.round((stats.pass / stats.total) * 1000) / 10 : 0;

    return stats;
  }, [getQualityStatus]);

  // Cleanup function for markers and clusterer
  const cleanupMarkers = useCallback(() => {
    if (markerClustererRef.current) {
      markerClustererRef.current.clearMarkers();
      markerClustererRef.current = null;
    }
    
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];
    
    if (infoWindowRef.current) {
      infoWindowRef.current.close();
    }
  }, []);

  // Create and manage markers with clustering
  const createMarkersWithClustering = useCallback((measurementsData) => {
    if (!mapRef.current || !isLoaded || !measurementsData.length) return;
    
    if (!window.google || !window.google.maps) {
      console.warn('Google Maps API not loaded yet');
      return;
    }
  
    // Ensure map is still mounted and visible
    if (activeTab !== 'Measurements') {
      return;
    }
  
    cleanupMarkers();
  
    if (!infoWindowRef.current) {
      infoWindowRef.current = new window.google.maps.InfoWindow();
    }
  
    const markers = measurementsData
      .filter(measurement => measurement.latitude && measurement.longitude)
      .slice(0, MAX_MAP_MARKERS)
      .map(measurement => {
        // Determine marker color based on quality status
        const qualityStatus = getQualityStatus(measurement, qualityAttribute, qualityThresholds);
        // Don't use activeMarkerId in the initial creation - we'll update colors later
        const markerIcon = qualityMarkerIcons[qualityStatus];
        
        const marker = new window.google.maps.Marker({
          position: { lat: measurement.latitude, lng: measurement.longitude },
          icon: markerIcon,
          title: measurement.Description1 || 'Measurement',
          map: null,
        });
  
        marker.addListener('click', () => {
          // Handle marker click logic here (same as before)
          setActiveMarkerId(measurement.id);
          
          markersRef.current.forEach(m => {
            const isActive = m.measurement?.id === measurement.id;
            if (isActive) {
              m.setIcon(activeMarkerIcon);
            } else {
              const status = getQualityStatus(m.measurement, qualityAttribute, qualityThresholds);
              m.setIcon(qualityMarkerIcons[status]);
            }
          });
  
          const attributeValue = measurement[qualityAttribute === 'compaction' ? 'Compaction' : 'Density'];
          const attributeLabel = qualityAttribute === 'compaction' ? 'Compaction' : 'Density';
          const statusColor = qualityStatus === 'pass' ? '#10B981' : qualityStatus === 'warning' ? '#F59E0B' : '#EF4444';
  
          const contentString = `
            <div class="p-3 bg-white rounded-lg shadow-md" style="min-width: 250px;">
              <div class="flex items-center justify-start mb-3">
                <h3 class="text-base font-bold text-gray-800">Measurement Details</h3>
              </div>
              <div class="space-y-2">
                <p class="text-sm">
                  <span class="font-medium text-gray-700">Density:</span>
                  <span class="ml-1 text-gray-900">${measurement.Density?.toFixed(1) || 'N/A'}</span>
                </p>
                <p class="text-sm">
                  <span class="font-medium text-gray-700">Compaction:</span>
                  <span class="ml-1 text-gray-900">${measurement.Compaction?.toFixed(1) || 'N/A'}%</span>
                </p>
                <p class="text-sm">
                  <span class="font-medium text-gray-700">Quality Status:</span>
                  <span class="ml-1 font-medium" style="color: ${statusColor}">
                    ${qualityStatus.charAt(0).toUpperCase() + qualityStatus.slice(1)} 
                    (${attributeLabel}: ${attributeValue?.toFixed(1) || 'N/A'})
                  </span>
                </p>
                <p class="text-sm">
                  <span class="font-medium text-gray-700">Surface Temp:</span>
                  <span class="ml-1 text-gray-900">${measurement.SurfaceTemp?.toFixed(1) || 'N/A'}</span>
                </p>
                <p class="text-sm">
                  <span class="font-medium text-gray-700">Operator:</span>
                  <span class="ml-1 text-gray-900">${measurement.operator_name || 'N/A'}</span>
                </p>
                <p class="text-sm">
                  <span class="font-medium text-gray-700">Time:</span>
                  <span class="ml-1 text-gray-900">${new Date(measurement.LocalTimeOfMeasurement).toLocaleString()}</span>
                </p>
              </div>
            </div>
          `;
  
          infoWindowRef.current.setContent(contentString);
          infoWindowRef.current.open(mapRef.current, marker);
        });
  
        marker.measurement = measurement;
        return marker;
      });
  
    markersRef.current = markers;
  
    if (markers.length > 0) {
      const clusterRenderer = createClusterRenderer();
      if (clusterRenderer) {
        markerClustererRef.current = new MarkerClusterer({
          map: mapRef.current,
          markers: markers,
          renderer: clusterRenderer,
          gridSize: 80,
          maxZoom: 14,
          minimumClusterSize: 3,
          averageCenter: true,
          zoomOnClick: true,
          imagePath: undefined,
        });
      }
    }
  }, [isLoaded, qualityAttribute, qualityThresholds, getQualityStatus, activeTab]); // Removed activeMarkerId from dependencies

  // Fetch organization member IDs
  useEffect(() => {
    const fetchInitialData = async () => {
      if (!user) return;

      try {
        let tempMemberIds = [user.id];
        
        const { data: orgMember, error: orgError } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', user.id)
          .single();

        if (orgError && orgError.code !== 'PGRST116') {
          console.error('Error fetching organization membership:', orgError);
          return;
        }

        if (orgMember) {
          setOrganizationId(orgMember.organization_id);
          
          const { data: orgMembers, error: orgMembersError } = await supabase
            .from('organization_members')
            .select('user_id')
            .eq('organization_id', orgMember.organization_id);

          if (orgMembersError) {
            console.error('Error fetching organization members:', orgMembersError);
            return;
          }

          tempMemberIds = orgMembers.map((member) => member.user_id);
        }

        setMemberIds(tempMemberIds);
      } catch (err) {
        console.error('Unexpected error fetching initial data:', err);
      }
    };

    fetchInitialData();
  }, [user]);

  // Fetch measurements using the optimized view
  const fetchMeasurements = useCallback(async (page = 1) => {
    if (!user || !projectId || memberIds.length === 0) return;

    setLoading(true);
    try {
      const from = (page - 1) * measurementsPerPage;
      const to = from + measurementsPerPage - 1;

      let query = supabase
        .from('measurements_with_details')
        .select('*', { count: 'exact' })
        .in('profile_id', memberIds)
        .eq('project_id', projectId)
        .limit(MAX_RECORDS);

      // Apply filters
      if (selectedOperator) {
        query = query.eq('operator_name', selectedOperator);
      }
      if (startDate) {
        query = query.gte('LocalTimeOfMeasurement', new Date(startDate).toISOString());
      }
      if (endDate) {
        query = query.lte('LocalTimeOfMeasurement', new Date(endDate).toISOString());
      }

      const { data, count, error } = await query
        .range(from, to)
        .order('LocalTimeOfMeasurement', { ascending: sortOrder === 'asc' });

      if (error) throw error;

      const measurementsData = data || [];
      setMeasurements(measurementsData);
      setTotalRecords(count || 0);

      // Extract unique operators from all data (not just current page)
      const uniqueOperators = Array.from(
        new Set(measurementsData.map(m => m.operator_name))
      ).filter(op => op && op.trim() !== 'Unknown').sort();
      setOperators(uniqueOperators);

      // Set map center
      if (measurementsData.length > 0 && measurementsData[0].latitude && measurementsData[0].longitude) {
        setMapCenter({ 
          lat: measurementsData[0].latitude, 
          lng: measurementsData[0].longitude 
        });
      }

    } catch (err) {
      console.error('Error fetching measurements:', err);
    } finally {
      setLoading(false);
    }
  }, [user, projectId, memberIds, selectedOperator, startDate, endDate, sortOrder, measurementsPerPage]);

  // Fetch map markers (separate from table data for performance)
  const fetchMapMarkers = useCallback(async () => {
    if (!user || !projectId || memberIds.length === 0) return;

    try {
      let query = supabase
        .from('measurements_with_details')
        .select('id, latitude, longitude, Density, Compaction, SurfaceTemp, operator_name, project_display_name, LocalTimeOfMeasurement, Description1')
        .in('profile_id', memberIds)
        .eq('project_id', projectId)
        .not('latitude', 'is', null)
        .not('longitude', 'is', null)
        .limit(MAX_MAP_MARKERS);

      // Apply same filters as table data
      if (selectedOperator) {
        query = query.eq('operator_name', selectedOperator);
      }
      if (startDate) {
        query = query.gte('LocalTimeOfMeasurement', new Date(startDate).toISOString());
      }
      if (endDate) {
        query = query.lte('LocalTimeOfMeasurement', new Date(endDate).toISOString());
      }

      const { data, error } = await query.order('LocalTimeOfMeasurement', { ascending: false });

      if (error) throw error;

      setMapMarkers(data || []);
      
      if (data && data.length > 5000) {
        console.log(`Loading ${data.length} markers with clustering...`);
      }
      
      createMarkersWithClustering(data || []);

    } catch (err) {
      console.error('Error fetching map markers:', err);
    }
  }, [user, projectId, memberIds, selectedOperator, startDate, endDate, createMarkersWithClustering]);

  // Fetch project data
  const fetchProjectData = useCallback(async () => {
    if (!projectId) return;
    try {
      const { data: projectData, error } = await supabase
        .from('projects')
        .select('*')
        .eq('id', projectId)
        .single();

      if (error) {
        console.error('Error fetching project data:', error);
      } else {
        setProjectData(projectData);
      }
    } catch (err) {
      console.error('Unexpected error fetching project data:', err);
    }
  }, [projectId]);

  // Fetch project polygon from the view
  useEffect(() => {
    const fetchProjectPolygon = async () => {
      if (!projectId || memberIds.length === 0) return;
      
      try {
        const { data, error } = await supabase
          .from('measurements_with_details')
          .select('project_geofence')
          .in('profile_id', memberIds)
          .eq('project_id', projectId)
          .not('project_geofence', 'is', null)
          .limit(1)
          .single();

        if (error || !data?.project_geofence) {
          setProjectPolygon([]);
          return;
        }

        const geoJson = typeof data.project_geofence === 'string' 
          ? JSON.parse(data.project_geofence) 
          : data.project_geofence;

        if (geoJson && geoJson.coordinates && geoJson.coordinates.length > 0) {
          const coords = geoJson.coordinates[0].map(([lng, lat]) => ({ lat, lng }));
          setProjectPolygon(coords);
        } else {
          setProjectPolygon([]);
        }
      } catch (err) {
        console.error('Error fetching project polygon:', err);
        setProjectPolygon([]);
      }
    };

    fetchProjectPolygon();
  }, [projectId, memberIds]);

  // Fetch organization integrations
  useEffect(() => {
    const fetchOrganizationIntegrations = async () => {
      if (!organizationId) return;
      
      try {
        const { data, error } = await supabase
          .from('organization_integrations')
          .select('integration_name')
          .eq('organization_id', organizationId);
        
        if (error) {
          console.error('Error fetching organization integrations:', error);
          return;
        }
        
        const integrationMap = {};
        data.forEach(item => {
          integrationMap[item.integration_name] = true;
        });
        
        setAvailableIntegrations(integrationMap);
      } catch (err) {
        console.error('Unexpected error fetching organization integrations:', err);
      }
    };
    
    fetchOrganizationIntegrations();
  }, [organizationId]);

  // Debounced fetch on filter changes
  useEffect(() => {
    if (!user || !projectId || memberIds.length === 0) return;

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchMeasurements(1);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [user, projectId, memberIds, selectedOperator, startDate, endDate, sortOrder, fetchMeasurements]);

  // Fetch map markers when filters change
  useEffect(() => {
    if (memberIds.length > 0) {
      fetchMapMarkers();
    }
  }, [memberIds, selectedOperator, startDate, endDate, fetchMapMarkers]);

  // Fetch on page change
  useEffect(() => {
    if (user && projectId && memberIds.length > 0) {
      fetchMeasurements(currentPage);
    }
  }, [currentPage, fetchMeasurements]);

  // Fetch project data
  useEffect(() => {
    fetchProjectData();
  }, [fetchProjectData]);

  // Fetch sent integrations for currently visible measurements
  useEffect(() => {
    const fetchSentIntegrations = async () => {
      const ids = measurements.map(m => m.id); // Use server-paginated measurements
      if (!ids.length) return setSentIntegrations({});
  
      const { data, error } = await supabase
        .from('integrations_log')
        .select('measurement_id, integration')
        .in('integration', integrations)
        .in('measurement_id', ids);
  
      if (!error) {
        const map = {};
        data.forEach(({ measurement_id, integration }) => {
          map[measurement_id] ??= new Set();
          map[measurement_id].add(integration);
        });
        setSentIntegrations(map);
      }
    };
    fetchSentIntegrations();
  }, [measurements, currentPage, showOnStationModal]);

  // Set document title
  useEffect(() => {
    document.title = `Project | TransTech Connect`;
    return () => {
      document.title = "TransTech Connect";
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupMarkers();
    };
  }, [cleanupMarkers]);

  // Click-away effects
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(event.target)) {
        setActionsDropdown(null);
      }
    };

    if (actionsDropdown !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [actionsDropdown]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdowns = document.querySelectorAll('[id^="dropdown-"]');
      let isOutside = true;
      dropdowns.forEach((dropdown) => {
        if (dropdown.contains(event.target)) {
          isOutside = false;
        }
      });
      if (isOutside && activeDropdown !== null) {
        setActiveDropdown(null);
      }
    };

    if (activeDropdown !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeDropdown]);

  // Checkbox management
  useLayoutEffect(() => {
    if (checkbox.current) {
      const isIndeterminate =
        selectedMeasurements.length > 0 && selectedMeasurements.length < measurements.length;
      setChecked(selectedMeasurements.length === measurements.length);
      setIndeterminate(isIndeterminate);
      checkbox.current.indeterminate = isIndeterminate;
    }
  }, [selectedMeasurements, measurements]);

  const toggleAll = () => {
    setSelectedMeasurements(checked || indeterminate ? [] : measurements);
    setChecked(!checked && !indeterminate);
    setIndeterminate(false);
  };

  const handleMarkerClick = (measurement) => {
    if (measurement.latitude && measurement.longitude && mapRef.current) {
      // Center the map on the measurement
      const position = { lat: measurement.latitude, lng: measurement.longitude };
      mapRef.current.panTo(position);
      
      // Optionally zoom to a closer level if too far out
      const currentZoom = mapRef.current.getZoom();
      if (currentZoom < 16) {
        mapRef.current.setZoom(16);
      }
      
      // Find the specific marker
      const targetMarker = markersRef.current.find(marker => 
        marker.measurement?.id === measurement.id
      );
      
      if (!targetMarker || !infoWindowRef.current) {
        console.warn('Target marker or info window not found');
        return;
      }
      
      // Update marker colors immediately without state changes
      markersRef.current.forEach(marker => {
        if (marker.measurement) {
          const isActive = marker.measurement.id === measurement.id;
          if (isActive) {
            marker.setIcon(activeMarkerIcon);
          } else {
            const status = getQualityStatus(marker.measurement, qualityAttribute, qualityThresholds);
            marker.setIcon(qualityMarkerIcons[status]);
          }
        }
      });
      
      // Prepare and show info window content
      const qualityStatus = getQualityStatus(measurement, qualityAttribute, qualityThresholds);
      const attributeValue = measurement[qualityAttribute === 'compaction' ? 'Compaction' : 'Density'];
      const attributeLabel = qualityAttribute === 'compaction' ? 'Compaction' : 'Density';
      const statusColor = qualityStatus === 'pass' ? '#10B981' : qualityStatus === 'warning' ? '#F59E0B' : '#EF4444';
  
      const contentString = `
        <div class="p-3 bg-white rounded-lg shadow-md" style="min-width: 250px;">
          <div class="flex items-center justify-start mb-3">
            <h3 class="text-base font-bold text-gray-800">Measurement Details</h3>
          </div>
          <div class="space-y-2">
            <p class="text-sm">
              <span class="font-medium text-gray-700">Density:</span>
              <span class="ml-1 text-gray-900">${measurement.Density?.toFixed(1) || 'N/A'}</span>
            </p>
            <p class="text-sm">
              <span class="font-medium text-gray-700">Compaction:</span>
              <span class="ml-1 text-gray-900">${measurement.Compaction?.toFixed(1) || 'N/A'}%</span>
            </p>
            <p class="text-sm">
              <span class="font-medium text-gray-700">Quality Status:</span>
              <span class="ml-1 font-medium" style="color: ${statusColor}">
                ${qualityStatus.charAt(0).toUpperCase() + qualityStatus.slice(1)} 
                (${attributeLabel}: ${attributeValue?.toFixed(1) || 'N/A'})
              </span>
            </p>
            <p class="text-sm">
              <span class="font-medium text-gray-700">Surface Temp:</span>
              <span class="ml-1 text-gray-900">${measurement.SurfaceTemp?.toFixed(1) || 'N/A'}</span>
            </p>
            <p class="text-sm">
              <span class="font-medium text-gray-700">Operator:</span>
              <span class="ml-1 text-gray-900">${measurement.operator_name || 'N/A'}</span>
            </p>
            <p class="text-sm">
              <span class="font-medium text-gray-700">Time:</span>
              <span class="ml-1 text-gray-900">${new Date(measurement.LocalTimeOfMeasurement).toLocaleString()}</span>
            </p>
          </div>
        </div>
      `;
  
      // Set content and open info window
      infoWindowRef.current.setContent(contentString);
      infoWindowRef.current.open(mapRef.current, targetMarker);
      
      // Update selected marker state for any other components that might use it
      setSelectedMarker({
        ...measurement,
        position: position,
      });
      
      // Only update activeMarkerId state after a delay to avoid triggering marker recreation
      setTimeout(() => {
        setActiveMarkerId(measurement.id);
      }, 100);
    }
  };

  const handleSendToOnStation = (measurementData) => {
    console.log('Measurements sent to OnStation:', measurementData);
  };

  const exportToCSV = (data, filename = "export.csv") => {
    if (!data || data.length === 0) {
      console.error("No data available to export.");
      return;
    }

    const columns = {
      "first_name": "First Name",
      "last_name": "Last Name",
      "operator_name": "Operator",
      "project_display_name": "Project",
      "Density": "Density",
      "Compaction": "Compaction",
      "SurfaceTemp": "Surface Temperature",
      "LocalTimeOfMeasurement": "Local Time",
      "latitude": "Latitude",
      "longitude": "Longitude",
      "Description1": "Description 1",
      "Description2": "Description 2",
      "Station": "Station"
    };

    const headers = Object.values(columns);
    const rows = data.map((row) =>
      Object.keys(columns).map((key) => {
        let value = row[key];
        if (typeof value === "string") {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value !== null && value !== undefined ? value : "";
      }).join(",")
    );

    const csvContent = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
    setSelectedMeasurements([]); // Clear measurements after export
  };

  const handleCloseWithReset = () => {
    setSelectedMeasurements([]);
    setShowOnStationModal(false);
  };

  const handleViewDetails = (measurement) => {
    setSelectedDetailsMeasurement(measurement);
    setShowDetailsModal(true);
    setActiveDropdown(null);
  };

  // Quality control handlers
  const handleQualityAttributeChange = (attribute) => {
    setQualityAttribute(attribute);
    setTempThresholds(qualityThresholds);
  };

  const handleThresholdChange = (attribute, type, value) => {
    setTempThresholds(prev => ({
      ...prev,
      [attribute]: {
        ...prev[attribute],
        [type]: parseFloat(value) || 0
      }
    }));
  };

  const applyQualitySettings = () => {
    setQualityThresholds(tempThresholds);
    setQualityControlExpanded(false);
  };

  const resetQualitySettings = () => {
    setTempThresholds(DEFAULT_THRESHOLDS);
    setQualityThresholds(DEFAULT_THRESHOLDS);
    setQualityAttribute('compaction');
  };

  // Update markers when quality settings change
  useEffect(() => {
    if (mapMarkers.length > 0) {
      createMarkersWithClustering(mapMarkers);
    }
  }, [qualityAttribute, qualityThresholds, mapMarkers.length]);

  // Recreate markers when returning to Measurements tab
  useEffect(() => {
    if (activeTab === 'Measurements' && mapMarkers.length > 0) {
      // Small delay to ensure map is ready
      const timer = setTimeout(() => {
        createMarkersWithClustering(mapMarkers);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [activeTab, mapMarkers, createMarkersWithClustering]);

  const handleStyleChange = (style) => {
    setMapStyle(style);
    if (mapRef.current) {
      mapRef.current.setOptions({ styles: style });
    }
  };

  if (!isLoaded) return <Layout><div>Loading Map...</div></Layout>;

  // Pagination calculations - use server-paginated data directly
  const totalPages = Math.ceil(totalRecords / measurementsPerPage);
  const currentMeasurements = measurements; // Already paginated by server

  // Calculate quality statistics for map markers
  const qualityStats = calculateQualityStats(mapMarkers, qualityAttribute, qualityThresholds);

  const handleDeleteMeasurement = async (measurement) => {
    if (!measurement) return;
    
    setDeletingMeasurement(true);
    try {
      // First, remove from project mapping if it exists
      const { error: mappingError } = await supabase
        .from('project_mapping')
        .delete()
        .eq('measurement_id', measurement.id);
      
      if (mappingError) {
        console.error('Error removing measurement from project mapping:', mappingError);
        // Continue with measurement deletion even if mapping removal fails
      }
      
      // Delete the measurement itself
      const { error: measurementError } = await supabase
        .from('measurements')
        .delete()
        .eq('id', measurement.id);
      
      if (measurementError) {
        throw measurementError;
      }
      
      // Update project geofence after deletion
      if (projectId) {
        await supabase.rpc('update_project_geofence', { p_project_id: projectId });
      }
      
      // Remove from local state
      setMeasurements(prev => prev.filter(m => m.id !== measurement.id));
      setMapMarkers(prev => prev.filter(m => m.id !== measurement.id));
      setSelectedMeasurements(prev => prev.filter(m => m.id !== measurement.id));
      setTotalRecords(prev => prev - 1);
      
      // Close modal and reset state
      setDeleteModalOpen(false);
      setMeasurementToDelete(null);
      
      // Recreate map markers to reflect the deletion
      setTimeout(() => {
        fetchMapMarkers();
      }, 100);
      
    } catch (error) {
      console.error('Error deleting measurement:', error);
      alert('Failed to delete measurement. Please try again.');
    } finally {
      setDeletingMeasurement(false);
    }
  };

  const initiateMeasurementDeletion = (measurement) => {
    setMeasurementToDelete(measurement);
    setDeleteModalOpen(true);
    setActiveDropdown(null);
  };

  return (
    <Layout>
      <div className="mb-4">
        <div>
          <button
            onClick={() => navigate('/projects')}
            className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
          >
            <ArrowLongLeftIcon className="mr-2 h-5 w-5" aria-hidden="true" />
            Back to Projects
          </button>
        </div>
        <div className="flex justify-between items-center pr-4">
          <h3 className="text-base font-semibold text-gray-900">
            {projectData ? projectData.name : 'Loading project name...'}
          </h3>
          {projectData && (
            <span
              className={classNames(
                projectData.active ? 'bg-green-100 text-green-800 border border-green-500' : 'bg-red-100 text-red-800 border border-red-500',
                'inline-flex items-center rounded-full px-3 py-1 text-sm font-medium'
              )}
            >
              {projectData.active ? 'Active' : 'Inactive'}
            </span>
          )}
        </div>
        
        {/* Tab Navigation */}
        <div className="grid grid-cols-1 sm:hidden mt-2">
          <select
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value)}
            aria-label="Select a tab"
            className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-2 pl-3 pr-8 text-base text-gray-900 outline outline-1 outline-gray-300 focus:outline-2 focus:outline-yellow-600"
          >
            {['Measurements', 'Analytics', 'Details', 'Export History'].map((tab) => (
              <option key={tab} value={tab}>
                {tab}
              </option>
            ))}
          </select>
          <ChevronDownIcon
            aria-hidden="true"
            className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end fill-gray-500"
          />
        </div>
        <div className="hidden sm:block">
          <div className="border-b border-gray-200 mt-4">
            <nav aria-label="Tabs" className="-mb-px flex space-x-8">
              {['Measurements', 'Analytics', 'Details', 'Reports', 'Export History'].map((tab) => (
                <a
                  key={tab}
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveTab(tab);
                  }}
                  className={classNames(
                    activeTab === tab
                      ? 'border-yellow-500 text-yellow-600'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                    'whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium'
                  )}
                >
                  {tab}
                </a>
              ))}
            </nav>
          </div>
        </div>
      </div>

      {activeTab === 'Measurements' && (
        <>
          {/* Map Section */}
          <div className="mb-4 h-[65vh] border-2 rounded-lg">
            <div className="w-full h-full rounded-md overflow-hidden">
              <GoogleMap
                mapContainerStyle={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '0.5rem',
                }}
                center={mapCenter}
                zoom={13}
                onLoad={(map) => {
                  mapRef.current = map;
                }}
                options={{ fullscreenControl: false, styles: mapStyle  }}
              >
                {/* Map controls */}
                <div className="absolute top-4 right-4 flex flex-col space-y-2">
                <div className="flex space-x-2 justify-end">
    <button
      onClick={() => handleStyleChange([])}
      className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
        mapStyle.length === 0 ? 'border-yellow-500 bg-gray-800' : 'border-gray-300 bg-gray-600'
      } hover:bg-gray-700 transition-all`}
      title="Default Mode"
    >
      <SunIcon className="w-6 h-6 text-white" />
    </button>
    <button
      onClick={() => handleStyleChange(nightMode)}
      className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
        mapStyle === nightMode ? 'border-yellow-500 bg-gray-800' : 'border-gray-300 bg-gray-600'
      } hover:bg-gray-700 transition-all`}
      title="Night Mode"
    >
      <MoonIcon className="w-6 h-6 text-white" />
    </button>
    <button
      onClick={() => handleStyleChange(aubergeine)}
      className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
        mapStyle === aubergeine ? 'border-yellow-500 bg-gray-800' : 'border-gray-300 bg-gray-600'
      } hover:bg-gray-700 transition-all`}
      title="Aubergeine Mode"
    >
      <BoltIcon className="w-6 h-6 text-white" />
    </button>
    <button
      onClick={() => handleStyleChange(retro)}
      className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
        mapStyle === retro ? 'border-yellow-500 bg-gray-800' : 'border-gray-300 bg-gray-600'
      } hover:bg-gray-700 transition-all`}
      title="Retro Mode"
    >
      <LightBulbIcon className="w-6 h-6 text-white" />
    </button>
  </div>
                  {/* Quality Control Card */}
                  <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                    {/* Collapsed View */}
                    {!qualityControlExpanded && (
                      <div className="px-4 py-3">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="text-lg font-bold text-gray-900">
                              {qualityStats.passRate}%
                            </div>
                            <div className="text-xs text-gray-600 mb-1">
                              {qualityAttribute === 'compaction' ? 'Compaction' : 'Density'} Pass Rate
                            </div>
                            <div className="flex items-center space-x-2 text-xs">
                              <span className="flex items-center">
                                <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
                                {qualityStats.pass}
                              </span>
                              <span className="flex items-center">
                                <div className="w-2 h-2 bg-orange-500 rounded-full mr-1"></div>
                                {qualityStats.warning}
                              </span>
                              <span className="flex items-center">
                                <div className="w-2 h-2 bg-red-500 rounded-full mr-1"></div>
                                {qualityStats.fail}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={() => setQualityControlExpanded(true)}
                            className="ml-3 p-1 text-gray-400 hover:text-gray-600 focus:outline-none"
                            title="Expand quality controls"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                        </div>
                        
                        {/* Additional info */}
                        <div className="mt-2 pt-2 border-t border-gray-200">
                          <div className="text-xs text-gray-600">
                            {mapMarkers.length.toLocaleString()} total markers
                          </div>
                          {mapMarkers.length >= MAX_MAP_MARKERS && (
                            <div className="text-xs text-orange-600 mt-1">
                              Limited to {MAX_MAP_MARKERS.toLocaleString()}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Expanded View */}
                    {qualityControlExpanded && (
                      <div className="px-4 py-3" style={{ minWidth: '320px' }}>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-sm font-semibold text-gray-900">Quality Control</h3>
                          <button
                            onClick={() => {
                              setTempThresholds(qualityThresholds);
                              setQualityControlExpanded(false);
                            }}
                            className="p-1 text-gray-400 hover:text-gray-600 focus:outline-none"
                            title="Collapse"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                            </svg>
                          </button>
                        </div>

                        {/* Attribute Selector */}
                        <div className="mb-4">
                          <label className="block text-xs font-medium text-gray-700 mb-2">Map Attribute</label>
                          <select
                            value={qualityAttribute}
                            onChange={(e) => handleQualityAttributeChange(e.target.value)}
                            className="w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          >
                            <option value="compaction">Compaction (%)</option>
                            <option value="density">Density (pcf)</option>
                          </select>
                        </div>

                        {/* Threshold Settings */}
                        <div className="space-y-3 mb-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              <span className="flex items-center">
                                <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                                Pass Threshold (≥)
                              </span>
                            </label>
                            <input
                              type="number"
                              step="0.1"
                              value={tempThresholds[qualityAttribute].pass}
                              onChange={(e) => handleThresholdChange(qualityAttribute, 'pass', e.target.value)}
                              className="w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              <span className="flex items-center">
                                <div className="w-2 h-2 bg-orange-500 rounded-full mr-2"></div>
                                Warning Threshold (≥)
                              </span>
                            </label>
                            <input
                              type="number"
                              step="0.1"
                              value={tempThresholds[qualityAttribute].warning}
                              onChange={(e) => handleThresholdChange(qualityAttribute, 'warning', e.target.value)}
                              className="w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                            />
                          </div>
                        </div>

                        {/* Current Stats */}
                        <div className="mb-4 p-2 bg-gray-50 rounded-md">
                          <div className="text-xs font-medium text-gray-700 mb-1">Current Results</div>
                          <div className="grid grid-cols-4 gap-2 text-xs">
                            <div className="text-center">
                              <div className="font-medium text-green-600">{qualityStats.pass}</div>
                              <div className="text-gray-500">Pass</div>
                            </div>
                            <div className="text-center">
                              <div className="font-medium text-orange-600">{qualityStats.warning}</div>
                              <div className="text-gray-500">Warning</div>
                            </div>
                            <div className="text-center">
                              <div className="font-medium text-red-600">{qualityStats.fail}</div>
                              <div className="text-gray-500">Fail</div>
                            </div>
                            <div className="text-center">
                              <div className="font-medium text-gray-600">{qualityStats.unknown}</div>
                              <div className="text-gray-500">Unknown</div>
                            </div>
                          </div>
                          <div className="mt-2 text-center">
                            <div className="text-sm font-semibold text-gray-900">{qualityStats.passRate}% Pass Rate</div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex space-x-2">
                          <button
                            onClick={applyQualitySettings}
                            className="flex-1 bg-blue-600 text-white text-xs font-medium py-2 px-3 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            Apply
                          </button>
                          <button
                            onClick={resetQualitySettings}
                            className="flex-1 bg-gray-200 text-gray-700 text-xs font-medium py-2 px-3 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                          >
                            Reset
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Project polygon */}
                {projectPolygon.length > 0 && (
                  <PolygonF
                    paths={projectPolygon}
                    options={{
                      strokeColor: '#FF0000',
                      strokeOpacity: 0.8,
                      strokeWeight: 2,
                      fillColor: '#FF0000',
                      fillOpacity: 0.35,
                    }}
                  />
                )}
              </GoogleMap>
            </div>
          </div>

          {/* Performance warnings */}
          {totalRecords > 15000 && (
            <div className="mb-4 p-3 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded-md">
              <p className="text-sm">
                <strong>Note:</strong> Showing {totalRecords.toLocaleString()} records. 
                Map displays up to {MAX_MAP_MARKERS.toLocaleString()} markers with clustering for optimal performance.
              </p>
            </div>
          )}
          
          {mapMarkers.length >= MAX_MAP_MARKERS && (
            <div className="mb-4 p-3 bg-blue-100 border border-blue-400 text-blue-700 rounded-md">
              <p className="text-sm">
                <strong>Map Performance:</strong> Displaying {MAX_MAP_MARKERS.toLocaleString()} of {totalRecords.toLocaleString()} markers with smart clustering. 
                Use filters for focused viewing of specific areas.
              </p>
            </div>
          )}

          {/* Filters Section */}
          <div className="sm:flex sm:items-center justify-between mb-4">
            <div className="sm:flex sm:items-center sm:space-x-4">
              {/* Sort Order Dropdown */}
              <div className="w-full sm:w-auto">
                <select
                  id="sortOrder"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
                >
                  <option value="desc">Newest to Oldest</option>
                  <option value="asc">Oldest to Newest</option>
                </select>
              </div>

              {/* Operator Filter Dropdown */}
              <div className="w-full sm:w-auto">
                <select
                  id="operatorFilter"
                  value={selectedOperator}
                  onChange={(e) => setSelectedOperator(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
                >
                  <option value="">All Operators</option>
                  {operators.map((operator, index) => (
                    <option key={index} value={operator}>
                      {operator}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date Range Filter */}
              <div className="flex items-center space-x-2">
                <input
                  type="date"
                  id="startDate"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="block w-36 rounded-l-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
                  placeholder="Start Date"
                />
                <div className="flex items-center px-2 text-gray-500">/</div>
                <input
                  type="date"
                  id="endDate"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="block w-36 rounded-r-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
                  placeholder="End Date"
                />
              </div>
            </div>

            {/* Actions Dropdown */}
            <div className="relative ml-auto">
              <button
                className={`inline-flex items-center rounded-md px-4 py-2 text-sm font-semibold shadow-sm ${
                  selectedMeasurements.length > 0
                    ? actionSuccess
                      ? "bg-green-600 text-white"
                      : "bg-yellow-500 text-white hover:bg-yellow-600"
                    : "bg-gray-400 text-white cursor-not-allowed"
                }`}
                onClick={(e) => {
                  if (selectedMeasurements.length === 0) return;
                  e.stopPropagation();
                  if (!actionSuccess) {
                    setActionsDropdown(actionsDropdown === "actions" ? null : "actions");
                  }
                }}
                disabled={selectedMeasurements.length === 0 || actionSuccess}
              >
                {actionSuccess ? (
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                      className="mr-2 h-5 w-5"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4" />
                    </svg>
                    Successful
                  </>
                ) : (
                  <>
                    Actions
                    <svg
                      className="ml-2 h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 11-1.414 1.414L11 7.414V14a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </>
                )}
              </button>

              {actionsDropdown === "actions" && (
                <div
                  id="actions-dropdown"
                  className="absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded-md shadow-lg z-10"
                  ref={actionsMenuRef}
                >
                  {/* <button
                    className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActionsDropdown(null);
                      setShowReportBuilder(true);
                    }}
                  >
                    Generate Report
                  </button> */}
                 
                  <Tooltip 
                    message={!availableIntegrations['OnStation'] 
                      ? 'Please configure OnStation integration in organization settings' 
                      : ''}
                  >
                    <button
                      className={`block w-full px-4 py-2 text-left text-sm ${
                        availableIntegrations['OnStation'] 
                          ? 'text-gray-700 hover:bg-gray-100' 
                          : 'text-gray-400 cursor-not-allowed'
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (availableIntegrations['OnStation']) {
                          setActionsDropdown(null);
                          setShowOnStationModal(true);
                        }
                      }}
                    >
                      Send to OnStation
                    </button>
                  </Tooltip>
        
                  <button
                    className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActionsDropdown(null);
                      exportToCSV(selectedMeasurements, "selected_measurements.csv");
                    }}
                  >
                    Export to CSV
                  </button>

                  
                </div>
              )}
            </div>
          </div>

          {/* Measurements Table */}
          <div className="flow-root overflow-visible">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <div className="relative rounded-lg overflow-visible border">
                  <table className="min-w-full table-fixed divide-y divide-gray-300 rounded-lg border overflow-hidden">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="relative px-7 sm:w-12 sm:px-6">
                          <input
                            type="checkbox"
                            className="absolute left-4 top-1/2 -mt-2 size-4 rounded border-gray-300 text-yellow-600 focus:ring-yellow-600"
                            ref={checkbox}
                            checked={checked && selectedMeasurements.length > 0}
                            onChange={toggleAll}
                          />
                        </th>
                        <th className="min-w-[9rem] py-3.5 pr-3 text-left text-sm font-semibold text-gray-900">
                          Description
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Operator
                        </th>
                        <th className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                          Density
                        </th>
                        <th className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                          Compaction
                        </th>
                        <th className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                          Surface Temp
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Local Time
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Integrations
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {loading ? (
                        <tr>
                          <td colSpan="9" className="py-6 text-center text-sm text-gray-600">
                            <div className="flex items-center justify-center">
                              <svg className="animate-spin h-5 w-5 mr-3 text-gray-400" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                              </svg>
                              Loading measurements...
                            </div>
                          </td>
                        </tr>
                      ) : currentMeasurements.length === 0 ? (
                        <tr>
                          <td colSpan="9" className="py-6 text-center text-sm text-gray-600 font-medium">
                            <svg className="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            No measurements found. Try adjusting your filters.
                          </td>
                        </tr>
                      ) : (
                        currentMeasurements.map((measurement) => (
                          <tr key={measurement.id} className={selectedMeasurements.includes(measurement) ? 'bg-gray-50' : undefined}>
                            <td className="relative px-7 sm:w-12 sm:px-6">
                              {selectedMeasurements.includes(measurement) && (
                                <div className="absolute inset-y-0 left-0 w-0.5 bg-yellow-600" />
                              )}
                              <input
                                type="checkbox"
                                className="absolute left-4 top-1/2 -mt-2 size-4 rounded border-gray-300 text-yellow-600 focus:ring-yellow-600"
                                value={measurement.id}
                                checked={selectedMeasurements.includes(measurement)}
                                onChange={(e) =>
                                  setSelectedMeasurements(
                                    e.target.checked
                                      ? [...selectedMeasurements, measurement]
                                      : selectedMeasurements.filter((m) => m !== measurement)
                                  )
                                }
                              />
                            </td>
                            <td className="whitespace-nowrap py-4 pr-3 text-sm font-medium text-gray-900">
                              {measurement.Description1 || 'No Description'}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">
                              {measurement.operator_name || 'Unknown'}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
                              <span className={`${
                                qualityAttribute === 'density' && measurement.Density !== null && measurement.Density !== undefined
                                  ? getQualityStatus(measurement, 'density', qualityThresholds) === 'pass' 
                                    ? 'text-green-600 font-medium'
                                    : getQualityStatus(measurement, 'density', qualityThresholds) === 'warning'
                                    ? 'text-orange-600 font-medium'
                                    : getQualityStatus(measurement, 'density', qualityThresholds) === 'fail'
                                    ? 'text-red-600 font-medium'
                                    : 'text-gray-500'
                                  : 'text-gray-500'
                              }`}>
                                {measurement.Density?.toFixed(1) || 'N/A'}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
                              <span className={`${
                                qualityAttribute === 'compaction' && measurement.Compaction !== null && measurement.Compaction !== undefined
                                  ? getQualityStatus(measurement, 'compaction', qualityThresholds) === 'pass' 
                                    ? 'text-green-600 font-medium'
                                    : getQualityStatus(measurement, 'compaction', qualityThresholds) === 'warning'
                                    ? 'text-orange-600 font-medium'
                                    : getQualityStatus(measurement, 'compaction', qualityThresholds) === 'fail'
                                    ? 'text-red-600 font-medium'
                                    : 'text-gray-500'
                                  : 'text-gray-500'
                              }`}>
                                {measurement.Compaction?.toFixed(1) || 'N/A'}
                                {measurement.Compaction ? '%' : ''}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center">
                              {measurement.SurfaceTemp?.toFixed(1) || 'N/A'}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              {new Date(measurement.LocalTimeOfMeasurement).toLocaleString()}
                            </td>
                            <td className="px-3 py-3.5 text-sm text-gray-500">
                              <div className="inline-flex flex-col items-start gap-y-1">
                                {integrations.map(name =>
                                  sentIntegrations[measurement.id]?.has(name) ? (
                                    <span
                                      key={name}
                                      className={classNames(
                                        'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium',
                                        name === 'HaulHub'
                                          ? 'bg-purple-100 text-purple-800'
                                          : 'bg-green-100 text-green-800'
                                      )}
                                    >
                                      ✓ {name}
                                    </span>
                                  ) : null
                                )}
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              <div id={`dropdown-${measurement.id}`} className="relative">
                                <button
                                  className="rounded-full p-2 text-gray-600 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveDropdown(activeDropdown === measurement.id ? null : measurement.id);
                                  }}
                                >
                                  <EllipsisVerticalIcon className="w-5 h-5" />
                                </button>
                                {activeDropdown === measurement.id && (
                                  <div className="absolute top-1/2 left-0 transform -translate-y-1/2 -translate-x-full w-36 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                                    <button
                                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                      onClick={() => {
                                        setActiveDropdown(null);
                                        handleMarkerClick(measurement);
                                      }}
                                    >
                                      View on Map
                                    </button>
                                    <button
                                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                      onClick={() => handleViewDetails(measurement)}
                                    >
                                      View Details
                                    </button>
                                    <button
                    className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    onClick={() => initiateMeasurementDeletion(measurement)}
                  >
                    Delete
                  </button>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                         
                        ))
                        
                      )}
                       <tr className="border-t border-gray-200">
        <td className="px-7 py-4"></td>
        <td className="py-4 pr-3"></td>
        <td className="px-3 py-4"></td>
        <td className="px-3 py-4"></td>
        <td className="px-3 py-4"></td>
        <td className="px-3 py-4"></td>
        <td className="px-3 py-4"></td>
        <td className="px-3 py-4"></td>
        <td className="px-3 py-4"></td>
      </tr>
    
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                <nav className="flex items-center justify-between border-t border-gray-200 px-4 sm:px-0 my-4 overflow-visible">
                  <div className="-mt-px flex w-0 flex-1">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="inline-flex items-center border-t-2 border-transparent pr-1 pt-4 text-sm font-medium text-gray-500 hover:border-gray-300 hover:text-gray-700 disabled:opacity-50"
                    >
                      <ArrowLongLeftIcon aria-hidden="true" className="mr-3 size-5 text-gray-400" />
                      Previous
                    </button>
                  </div>
                  <div className="hidden md:-mt-px md:flex">
                    {(() => {
                      const maxVisiblePages = 5;
                      const pageNumbers = [];

                      if (totalPages <= maxVisiblePages) {
                        for (let i = 1; i <= totalPages; i++) {
                          pageNumbers.push(i);
                        }
                      } else {
                        const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                        const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

                        if (startPage > 1) {
                          pageNumbers.push(1);
                          if (startPage > 2) pageNumbers.push('...');
                        }

                        for (let i = startPage; i <= endPage; i++) {
                          pageNumbers.push(i);
                        }

                        if (endPage < totalPages) {
                          if (endPage < totalPages - 1) pageNumbers.push('...');
                          pageNumbers.push(totalPages);
                        }
                      }

                      return pageNumbers.map((page, index) =>
                        page === '...' ? (
                          <span key={`ellipsis-${index}`} className="inline-flex items-center px-4 pt-4 text-sm font-medium text-gray-500">
                            ...
                          </span>
                        ) : (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={classNames(
                              currentPage === page
                                ? 'border-yellow-500 text-yellow-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                              'inline-flex items-center border-t-2 px-4 pt-4 text-sm font-medium'
                            )}
                          >
                            {page}
                          </button>
                        )
                      );
                    })()}
                  </div>
                  <div className="-mt-px flex w-0 flex-1 justify-end">
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="inline-flex items-center border-t-2 border-transparent pl-1 pt-4 text-sm font-medium text-gray-500 hover:border-gray-300 hover:text-gray-700 disabled:opacity-50"
                    >
                      Next
                      <ArrowLongRightIcon aria-hidden="true" className="ml-3 size-5 text-gray-400" />
                    </button>
                  </div>
                </nav>
                
                {/* Record count */}
                <div className="flex items-center justify-center text-sm text-gray-700 mt-2">
                  <span>{totalRecords.toLocaleString()} total records</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Other tabs */}
      {activeTab === 'Details' && <DetailsTab project={projectData} onUpdateSuccess={fetchProjectData} />}
      {activeTab === 'Documentation' && (
        <ProjectFiles organizationId={organizationId} projectId={projectId} />
      )}
      {activeTab === 'Export History' && (
        projectId && user?.id && currentMeasurements.length > 0 ? (
          <ExportHistory 
            projectId={projectId} 
            userId={user.id} 
            measurements={currentMeasurements}
          />
        ) : (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            <span className="ml-3 text-gray-700">Loading export history...</span>
          </div>
        )
      )}
      {activeTab === 'Analytics' && <ProjectAnalytics />}

      {activeTab === 'Reports' && (
  <DensityReportGenerator
    projectData={{
      project_id: projectId,
      project_display_name: projectData?.name,
      project_name: projectData?.name,
      project_description: projectData?.description,
    }}
    measurements={mapMarkers} // Use all map markers or filtered measurements
    onGenerateReport={handleGenerateReport}
    isLoading={reportGenerating}
  />
)}

      {/* Modals */}
      {showReportBuilder && (
        <ReportBuilder
          measurements={selectedMeasurements}
          onClose={() => setShowReportBuilder(false)}
          organizationId={organizationId}
          projectId={projectId}
        />
      )}

      {showOnStationModal && (
        <OnStationModal
          isOpen={showOnStationModal}
          onClose={() => setShowOnStationModal(false)}
          onCloseWithReset={handleCloseWithReset}
          selectedMeasurements={selectedMeasurements}
          onSubmit={handleSendToOnStation}
        />
      )}

      {showDetailsModal && selectedDetailsMeasurement && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-70">
          <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center mb-6 pb-3 border-b border-gray-200">
              <h2 className="text-2xl font-semibold text-gray-900">Measurement Details</h2>
              <button
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                onClick={() => setShowDetailsModal(false)}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-3">
                <div className="flex items-start">
                  <span className="w-1/3 font-medium text-gray-700 text-sm">Project:</span>
                  <span className="flex-1 text-gray-900 text-sm">{selectedDetailsMeasurement.project_display_name || 'N/A'}</span>
                </div>
                <div className="flex items-start">
                  <span className="w-1/3 font-medium text-gray-700 text-sm">Operator:</span>
                  <span className="flex-1 text-gray-900 text-sm">{selectedDetailsMeasurement.operator_name || 'Unknown'}</span>
                </div>
                <div className="flex items-start">
                  <span className="w-1/3 font-medium text-gray-700 text-sm">Density:</span>
                  <span className="flex-1 text-gray-900 text-sm">{selectedDetailsMeasurement.Density?.toFixed(1) || 'N/A'}</span>
                </div>
                <div className="flex items-start">
                  <span className="w-1/3 font-medium text-gray-700 text-sm">Compaction:</span>
                  <span className="flex-1 text-gray-900 text-sm">{selectedDetailsMeasurement.Compaction?.toFixed(1) || 'N/A'}%</span>
                </div>
                <div className="flex items-start">
                  <span className="w-1/3 font-medium text-gray-700 text-sm">Surface Temp:</span>
                  <span className="flex-1 text-gray-900 text-sm">{selectedDetailsMeasurement.SurfaceTemp?.toFixed(1) || 'N/A'}</span>
                </div>
                <div className="flex items-start">
                  <span className="w-1/3 font-medium text-gray-700 text-sm">Local Time:</span>
                  <span className="flex-1 text-gray-900 text-sm">{new Date(selectedDetailsMeasurement.LocalTimeOfMeasurement).toLocaleString()}</span>
                </div>
                <div className="flex items-start">
                  <span className="w-1/3 font-medium text-gray-700 text-sm">Station:</span>
                  <span className="flex-1 text-gray-900 text-sm">{selectedDetailsMeasurement.Station || 'N/A'}</span>
                </div>
                <div className="flex items-start">
                  <span className="w-1/3 font-medium text-gray-700 text-sm">Description 1:</span>
                  <span className="flex-1 text-gray-900 text-sm">{selectedDetailsMeasurement.Description1 || 'N/A'}</span>
                </div>
                <div className="flex items-start">
                  <span className="w-1/3 font-medium text-gray-700 text-sm">Description 2:</span>
                  <span className="flex-1 text-gray-900 text-sm">{selectedDetailsMeasurement.Description2 || 'N/A'}</span>
                </div>
                <div className="flex items-start">
                  <span className="w-1/3 font-medium text-gray-700 text-sm">Latitude:</span>
                  <span className="flex-1 text-gray-900 text-sm">
                    {selectedDetailsMeasurement.latitude?.toFixed(5) || 'N/A'}
                  </span>
                </div>
                <div className="flex items-start">
                  <span className="w-1/3 font-medium text-gray-700 text-sm">Longitude:</span>
                  <span className="flex-1 text-gray-900 text-sm">
                    {selectedDetailsMeasurement.longitude?.toFixed(5) || 'N/A'}
                  </span>
                </div>
              </div>
            </div>
            <div className="mt-8 flex justify-end">
              <button
                className="px-5 py-2 bg-yellow-600 text-white text-sm font-semibold rounded-lg shadow-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 transition-all duration-200"
                onClick={() => setShowDetailsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

{deleteModalOpen && measurementToDelete && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-70">
    <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
      <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
        <ExclamationCircleIcon className="h-8 w-8 text-red-600" />
      </div>
      
      <h3 className="text-lg font-medium text-center text-gray-900 mb-4">
        Delete Measurement
      </h3>
      
      <div className="mb-6">
        <p className="text-sm text-gray-500 text-center mb-4">
          Are you sure you want to delete this measurement? This action cannot be undone.
        </p>
        
        <div className="bg-gray-50 rounded-lg p-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="font-medium text-gray-700">Description:</span>
            <span className="text-gray-900">{measurementToDelete.Description1 || 'No Description'}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="font-medium text-gray-700">Operator:</span>
            <span className="text-gray-900">{measurementToDelete.operator_name || 'Unknown'}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="font-medium text-gray-700">Date:</span>
            <span className="text-gray-900">
              {new Date(measurementToDelete.LocalTimeOfMeasurement).toLocaleDateString()}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="font-medium text-gray-700">Compaction:</span>
            <span className="text-gray-900">{measurementToDelete.Compaction?.toFixed(1) || 'N/A'}%</span>
          </div>
        </div>
      </div>
      
      <div className="flex justify-center space-x-4">
        <button
          onClick={() => {
            setDeleteModalOpen(false);
            setMeasurementToDelete(null);
          }}
          disabled={deletingMeasurement}
          className="px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={() => handleDeleteMeasurement(measurementToDelete)}
          disabled={deletingMeasurement}
          className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center"
        >
          {deletingMeasurement ? (
            <>
              <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
              Deleting...
            </>
          ) : (
            'Delete Measurement'
          )}
        </button>
      </div>
    </div>
  </div>
)}
    </Layout>
  );
}